import React, { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Server,
  Lock,
  User,
  FolderOpen,
  Check,
  X,
  RefreshCw,
  Loader2,
  Play,
  StopCircle,
  FileText,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  Copy,
  AlertTriangle,
  Globe,
  Key,
  Hash,
  ChevronDown,
  ChevronRight
} from 'lucide-react'
import { adminApi } from '../../services/api'

export default function FTPConfig() {
  const queryClient = useQueryClient()

  // FTP Configuration State
  const [ftpConfig, setFtpConfig] = useState({
    host: '127.0.0.1',
    port: 21,
    username: '',
    password: '',
    remote_path: '/',
    use_tls: false
  })

  const [showPassword, setShowPassword] = useState(false)
  const [connectionTested, setConnectionTested] = useState(false)
  const [connectionResult, setConnectionResult] = useState(null)
  const [currentJobId, setCurrentJobId] = useState(null)
  const [extractionProgress, setExtractionProgress] = useState(null)
  const [isExpanded, setIsExpanded] = useState(false)

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: (config) => adminApi.testFtpConnection(config),
    onSuccess: (response) => {
      setConnectionTested(true)
      setConnectionResult(response.data)
    },
    onError: (error) => {
      setConnectionTested(true)
      setConnectionResult({
        success: false,
        message: error.response?.data?.detail || 'Connection failed',
        files_found: 0,
        pdf_files_found: 0
      })
    }
  })

  // Start FTP processing mutation
  const startProcessingMutation = useMutation({
    mutationFn: (request) => adminApi.startFtpProcessing(request),
    onSuccess: (response) => {
      setCurrentJobId(response.data.job_id)
      setExtractionProgress({
        status: 'starting',
        total: response.data.progress?.total || 0,
        processed: 0,
        successful: 0,
        failed: 0,
        percent: 0,
        message: 'Starting FTP extraction...',
        duplicate_files: [],
        review_files: []
      })
    },
    onError: (error) => {
      alert(error.response?.data?.detail || 'Failed to start FTP processing')
    }
  })

  // Cancel processing mutation
  const cancelMutation = useMutation({
    mutationFn: (jobId) => adminApi.cancelFtpProcessing(jobId),
    onSuccess: () => {
      setExtractionProgress(prev => ({ ...prev, status: 'cancelled', message: 'Cancelled by user' }))
    }
  })

  // Fetch job status if we have a running job
  const { data: jobStatus } = useQuery({
    queryKey: ['ftpJobStatus', currentJobId],
    queryFn: () => adminApi.getJob(currentJobId).then(res => res.data),
    enabled: !!currentJobId,
    refetchInterval: 2000
  })

  // Update progress when job status changes
  useEffect(() => {
    if (jobStatus) {
      setExtractionProgress({
        status: jobStatus.status,
        total: jobStatus.progress?.total || 0,
        processed: jobStatus.progress?.processed || 0,
        successful: jobStatus.progress?.successful || 0,
        failed: jobStatus.progress?.failed || 0,
        percent: jobStatus.progress?.percent || 0,
        message: jobStatus.status_message,
        duplicate_files: jobStatus.duplicate_files || [],
        review_files: jobStatus.review_files || []
      })

      if (['completed', 'failed', 'cancelled'].includes(jobStatus.status)) {
        setTimeout(() => {
          setCurrentJobId(null)
          queryClient.invalidateQueries(['repositoryStatus'])
        }, 10000)
      }
    }
  }, [jobStatus, queryClient])

  const handleInputChange = (field, value) => {
    setFtpConfig(prev => ({ ...prev, [field]: value }))
    setConnectionTested(false)
    setConnectionResult(null)
  }

  const handleTestConnection = () => {
    testConnectionMutation.mutate(ftpConfig)
  }

  const handleStartExtraction = () => {
    if (!connectionResult?.success) {
      alert('Please test the FTP connection first')
      return
    }

    startProcessingMutation.mutate({
      ftp_config: ftpConfig,
      job_name: `FTP Extraction - ${ftpConfig.host} - ${new Date().toLocaleString()}`,
      batch_size: 10,
      max_workers: 2,
      use_ocr_fallback: true,
      use_ai_fallback: true,
      skip_existing: true
    })
  }

  const handleCancelExtraction = () => {
    if (currentJobId) {
      cancelMutation.mutate(currentJobId)
    }
  }

  const isExtracting = currentJobId && extractionProgress &&
    !['completed', 'failed', 'cancelled'].includes(extractionProgress.status)

  return (
    <div className="card mt-6">
      <div
        className="card-header cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Server className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium">FTP Server Connection</h3>
          </div>
          <button
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              setIsExpanded(!isExpanded)
            }}
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          Connect to an FTP server to extract PDFs from a remote location
        </p>
      </div>

      {isExpanded && (
      <div className="card-body space-y-6">
        {/* Connection Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Host */}
          <div>
            <label className="label flex items-center">
              <Globe className="w-4 h-4 mr-1 text-gray-400" />
              FTP Host
            </label>
            <input
              type="text"
              value={ftpConfig.host}
              onChange={(e) => handleInputChange('host', e.target.value)}
              placeholder="127.0.0.1 or ftp.example.com"
              className="input w-full"
              disabled={isExtracting}
            />
          </div>

          {/* Port */}
          <div>
            <label className="label flex items-center">
              <Hash className="w-4 h-4 mr-1 text-gray-400" />
              Port
            </label>
            <input
              type="number"
              value={ftpConfig.port}
              onChange={(e) => handleInputChange('port', parseInt(e.target.value) || 21)}
              placeholder="21"
              className="input w-full"
              disabled={isExtracting}
            />
          </div>

          {/* Username */}
          <div>
            <label className="label flex items-center">
              <User className="w-4 h-4 mr-1 text-gray-400" />
              Username
            </label>
            <input
              type="text"
              value={ftpConfig.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              placeholder="ftpuser"
              className="input w-full"
              disabled={isExtracting}
            />
          </div>

          {/* Password */}
          <div>
            <label className="label flex items-center">
              <Key className="w-4 h-4 mr-1 text-gray-400" />
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={ftpConfig.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="********"
                className="input w-full pr-10"
                disabled={isExtracting}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Remote Path */}
          <div className="md:col-span-2">
            <label className="label flex items-center">
              <FolderOpen className="w-4 h-4 mr-1 text-gray-400" />
              Remote Path
            </label>
            <input
              type="text"
              value={ftpConfig.remote_path}
              onChange={(e) => handleInputChange('remote_path', e.target.value)}
              placeholder="/"
              className="input w-full font-mono"
              disabled={isExtracting}
            />
            <p className="text-xs text-gray-500 mt-1">
              The directory on the FTP server containing PDF files
            </p>
          </div>

          {/* TLS Option */}
          <div className="md:col-span-2">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={ftpConfig.use_tls}
                onChange={(e) => handleInputChange('use_tls', e.target.checked)}
                className="mr-2"
                disabled={isExtracting}
              />
              <Lock className="w-4 h-4 mr-1 text-gray-400" />
              <span className="text-sm text-gray-700">Use TLS/SSL (FTPS)</span>
            </label>
          </div>
        </div>

        {/* Test Connection Button */}
        <div className="flex items-center gap-4">
          <button
            onClick={handleTestConnection}
            disabled={testConnectionMutation.isPending || !ftpConfig.host || !ftpConfig.username || isExtracting}
            className="btn btn-secondary"
          >
            {testConnectionMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Test Connection
          </button>

          {/* Connection Status */}
          {connectionTested && connectionResult && (
            <div className={`flex items-center px-4 py-2 rounded-lg ${
              connectionResult.success
                ? 'bg-green-50 text-green-700'
                : 'bg-red-50 text-red-700'
            }`}>
              {connectionResult.success ? (
                <>
                  <Check className="w-5 h-5 mr-2" />
                  <span>
                    Connected! Found {connectionResult.pdf_files_found} PDF files
                  </span>
                </>
              ) : (
                <>
                  <X className="w-5 h-5 mr-2" />
                  <span>{connectionResult.message}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* FTP Extraction Section */}
        {connectionResult?.success && (
          <div className="p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-lg mr-4">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">FTP Extraction</h4>
                  <p className="text-sm text-gray-600">
                    Extract {connectionResult.pdf_files_found} PDF files from FTP server
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mb-4">
              {!isExtracting ? (
                <button
                  onClick={handleStartExtraction}
                  disabled={startProcessingMutation.isPending}
                  className="btn bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700 px-6 py-3"
                >
                  {startProcessingMutation.isPending ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  Start FTP Extraction
                </button>
              ) : (
                <button
                  onClick={handleCancelExtraction}
                  className="btn bg-red-600 text-white hover:bg-red-700 px-6 py-3"
                >
                  <StopCircle className="w-5 h-5 mr-2" />
                  Stop Extraction
                </button>
              )}
            </div>

            {/* Extraction Progress */}
            {extractionProgress && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-gray-900">
                    {extractionProgress.status === 'running' ? 'Extracting from FTP...' :
                     extractionProgress.status === 'completed' ? 'FTP Extraction Complete!' :
                     extractionProgress.status === 'failed' ? 'Extraction Failed' :
                     extractionProgress.status === 'cancelled' ? 'Extraction Cancelled' :
                     'Starting...'}
                  </span>
                  <span className="text-sm text-gray-600">
                    {extractionProgress.processed} / {extractionProgress.total} files
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full transition-all duration-500 ${
                      extractionProgress.status === 'completed' ? 'bg-green-500' :
                      extractionProgress.status === 'failed' ? 'bg-red-500' :
                      extractionProgress.status === 'cancelled' ? 'bg-yellow-500' :
                      'bg-gradient-to-r from-blue-500 to-cyan-500'
                    }`}
                    style={{ width: `${extractionProgress.percent}%` }}
                  />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <p className="text-lg font-bold text-green-600">{extractionProgress.successful}</p>
                    <p className="text-xs text-green-700">Successful</p>
                  </div>
                  <div className="p-2 bg-red-50 rounded-lg">
                    <p className="text-lg font-bold text-red-600">{extractionProgress.failed}</p>
                    <p className="text-xs text-red-700">Failed</p>
                  </div>
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <p className="text-lg font-bold text-blue-600">{extractionProgress.percent.toFixed(0)}%</p>
                    <p className="text-xs text-blue-700">Progress</p>
                  </div>
                </div>

                {extractionProgress.message && (
                  <p className="mt-3 text-sm text-gray-600 text-center">
                    {extractionProgress.message}
                  </p>
                )}

                {/* Duplicate Files Section */}
                {extractionProgress.duplicate_files?.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-2">
                      <Copy className="w-4 h-4 text-gray-500 mr-2" />
                      <span className="font-medium text-gray-700">
                        Duplicate Files Detected ({extractionProgress.duplicate_files.length})
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      These files have identical content to already processed files and were skipped:
                    </p>
                    <ul className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                      {extractionProgress.duplicate_files.map((file, idx) => (
                        <li key={idx} className="flex items-center">
                          <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                          {file}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Review Files Section */}
                {extractionProgress.review_files?.length > 0 && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center mb-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mr-2" />
                      <span className="font-medium text-amber-700">
                        Files Needing Review ({extractionProgress.review_files.length})
                      </span>
                    </div>
                    <p className="text-xs text-amber-600 mb-2">
                      These files have low extraction confidence and may need manual verification:
                    </p>
                    <ul className="text-sm text-amber-700 space-y-1 max-h-32 overflow-y-auto">
                      {extractionProgress.review_files.map((file, idx) => (
                        <li key={idx} className="flex items-center">
                          <span className="w-2 h-2 bg-amber-500 rounded-full mr-2"></span>
                          {file}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Info text */}
            {!extractionProgress && (
              <div className="mt-4 text-sm text-gray-600">
                <p className="flex items-center">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Files will be downloaded temporarily for processing
                </p>
                <p className="flex items-center mt-1">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Uses OCR for scanned documents + AI for intelligent data extraction
                </p>
                <p className="flex items-center mt-1">
                  <Check className="w-4 h-4 text-green-500 mr-2" />
                  Duplicate files will be automatically detected and skipped
                </p>
              </div>
            )}
          </div>
        )}

        {/* Connection Details */}
        {connectionResult?.success && connectionResult.connection_details && (
          <div className="p-4 bg-gray-50 rounded-lg">
            <h5 className="font-medium text-gray-700 mb-2">Connection Details</h5>
            <div className="text-sm text-gray-600 space-y-1">
              <p><span className="font-medium">Server:</span> {connectionResult.connection_details.server_welcome}</p>
              <p><span className="font-medium">Current Directory:</span> {connectionResult.connection_details.current_directory}</p>
              <p><span className="font-medium">Total Files:</span> {connectionResult.files_found}</p>
              <p><span className="font-medium">PDF Files:</span> {connectionResult.pdf_files_found}</p>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  )
}
